import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import {
  greaterOrEqual,
  lessOrEqual,
  nearlyEqual,
  roundTo,
} from '../../../../utils/geometry/epsilon';
import { message } from 'antd';
import {
  evaluateExpression,
  getInputType,
  isValidExpression,
} from '../../../../utils/string/inputExpression';
import { openMessageError } from '../../../../components/common/Message/openMessageError';
import { GeometryUtils } from '../../../../utils/geometry/GeometryUtils';
import i18n from '../../../../languages/i18n';
import { InsideType } from '../../../../constants/enum';

/**
 * PositionController - Quản lý vị trí, khoảng cách và hiển thị thông tin cho các modules 3D
 *
 * Chức năng chính:
 * - Quản lý vị trí và bounds của modules
 * - Tính toán khoảng cách giữa modules
 * - Hiển thị lines và labels đo khoảng cách (sử dụng CSS2DObject)
 * - Hỗ trợ rotation và collision detection
 */
export default class PositionController {
  /**
   * Khởi tạo PositionController
   * @param {THREE.Scene} scene - Scene Three.js để thêm objects
   * @param {CSS2DRenderer} labelRenderer - CSS2DRenderer instance
   */
  constructor(extendedSceneManager, labelRenderer = null) {
    this.selectedModule = null; // Module đang được chọn

    // Distance visualization objects
    this.distanceLines = new Map(); // Line objects cho distance
    this.distanceLabels = new Map(); // CSS2D labels cho distance
    this.boundingBoxHelpers = new Map(); // Bounding box helpers

    this.extendedSceneManager = extendedSceneManager;
    this.labelRenderer = labelRenderer; // CSS2DRenderer instance

    // Performance optimization: Cache frequently used objects
    this._tempVector3 = new THREE.Vector3();
    this._tempMatrix4 = new THREE.Matrix4();

    // Cache mesh bounds cho collision detection: uuid -> { stamp, bounds }
    this._meshBoundsCache = new Map();

    // Dùng lại khi duyệt tam giác của mesh lõm, tránh tạo object mỗi vòng lặp
    this._triA = new THREE.Vector3();
    this._triB = new THREE.Vector3();
    this._triC = new THREE.Vector3();
    this._triangle = new THREE.Triangle();
    this._shrunkBox = new THREE.Box3();

    // Constants
    this.CONSTANTS = {
      RAD_TO_DEG: 180 / Math.PI,
      DEG_TO_RAD: Math.PI / 180,
      LABEL_OFFSET: 0.3,
      DEBUG_POINT_SIZE: 0.05,
      LINE_WIDTH: 1,
    };

    // Initialize CSS styles for labels
    this.initializeLabelStyles();
  }

  /**
   * Initialize CSS styles for distance labels
   * Thêm styles vào document head nếu chưa có
   */
  initializeLabelStyles() {
    const styleId = 'controller-styles';

    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
                  .distance-label {
                      font-family: 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                      font-weight: 500;
                      font-size: 10px;
                      color: #000000;
                      background-color: rgba(255, 255, 255, 0.95);
                      padding: 2px 4px;
                      border-radius: 2px;
                      border: 0px solid rgba(0, 0, 0, 0.2);
                      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                      white-space: nowrap;
                      user-select: none;
                      pointer-events: auto;
                      cursor: pointer;
                      transition: all 0.2s ease;
                      z-index: 1000;
                      min-width: 20px;
                      text-align: center;
                      display: inline-block;
                  }
  
                  .distance-label:hover {
                      background-color: rgba(255, 255, 255, 1);
                      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
                      transform: scale(1.05);
                  }
  
                  /* Style cho label đang active */
                  .distance-label.active {
                      background-color: #ffd93d;
                      color: #000000;
                      font-weight: 600;
                  }
  
                  /* Style cho editing mode */
                  .distance-label.editing {
                      padding: 0;
                      background: transparent;
                      border: none;
                      box-shadow: none;
                  }
  
                  /* Input field cho editing */
                  .distance-input {
                      font-family: 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                      font-weight: 500;
                      font-size: 10px;
                      color: #000000;
                      background-color: #ffffff;
                      padding: 2px 4px;
                      border-radius: 4px;
                      border: 2px solid #FFC914;
                      outline: none;
                      width: 60px;
                      text-align: center;
                      transition: all 0.2s ease;
                  }
  
                  .distance-input:focus {
                      border-color: #FFC914;
                      transform: scale(1.05);
                  }
  
                  .distance-input.invalid {
                      border-color: #dc3545;
                      box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
                      animation: shake 0.3s;
                  }
  
                  @keyframes shake {
                      0%, 100% { transform: translateX(0); }
                      25% { transform: translateX(-5px); }
                      75% { transform: translateX(5px); }
                  }
  
                  @keyframes fadeIn {
                      from {
                          opacity: 0;
                          transform: scale(0.8);
                      }
                      to {
                          opacity: 1;
                          transform: scale(1);
                      }
                  }
  
                  .distance-label.new {
                      animation: fadeIn 0.3s ease-out;
                  }
              `;
      document.head.appendChild(style);
    }
  }

  /**
   * Set callback for distance change events
   */
  setOnDistanceChanged(callback) {
    this.onDistanceChanged = callback;
  }

  /**
   *
   */
  getModules() {
    const {
      walls,
      columns,
      beams,
      floor,
      ceiling,
      cabinetManager,
      createGroupPlane,
    } = this.extendedSceneManager;
    const modules = cabinetManager.currentCabinet.modules;
    // const groupPlanes = createGroupPlane.groupPlanes;

    const data = new Map();
    modules.forEach(item => {
      data.set(item.group.uuid, {
        ...item,
        type: 'module',
      });
    });
    // groupPlanes.forEach(item => {
    //   data.set(item.plane.uuid, {
    //     ...item,
    //     group: item.plane,
    //     type: 'groupPlane',
    //   });
    // });
    walls.forEach(item => {
      data.set(item.mesh.uuid, {
        ...item,
        type: 'wall',
        group: item.mesh,
      });
    });
    columns.forEach(item => {
      data.set(item.mesh.uuid, {
        ...item,
        type: 'column',
        group: item.mesh,
      });
    });
    beams.forEach(item => {
      data.set(item.mesh.uuid, {
        ...item,
        type: 'beam',
        group: item.mesh,
      });
    });
    data.set(floor.uuid, {
      type: 'floor',
      group: floor,
    });
    data.set(ceiling.uuid, {
      type: 'ceiling',
      group: ceiling,
    });

    return data;
  }

  /**
   * Chọn module để hiển thị distance lines
   * @param {string} moduleId - ID của module cần chọn
   * @returns {Object|null} Selected module data hoặc null
   */
  selectModule(moduleId) {
    const module = this.getModules().get(moduleId);
    if (!module) {
      console.warn(`Module ${moduleId} not found for selection`);
      return null;
    }

    this.selectedModule = module;
    return this.selectedModule;
  }

  /**
   * Bỏ chọn module hiện tại và xóa distance lines
   */
  deselectModule() {
    this.selectedModule = null;
    this.clearDistanceLines();
  }

  /**
   * Xóa module khỏi hệ thống quản lý
   */
  removeModule(moduleId) {
    if (this.selectedModule?.group?.uuid === moduleId) {
      this.deselectModule();
    } else if (this.selectedModule) {
      this.updateDistanceLines();
    }
  }

  /**
   * Xóa tất cả distance lines và labels
   */
  clearDistanceLines() {
    const linesToRemove = Array.from(this.distanceLines.values());
    const labelsToRemove = Array.from(this.distanceLabels.values());

    linesToRemove.forEach(line => {
      this.extendedSceneManager.scene.remove(line);
    });

    labelsToRemove.forEach(label => {
      this.extendedSceneManager.scene.remove(label);
      if (label.element) {
        label.element.remove();
      }
    });

    this.distanceLines.clear();
    this.distanceLabels.clear();
    this.clearBoundingBoxHelpers();
    // this.clearDebugPoints();
  }

  /**
   * Tạo bounding box helper để visualize
   * @param {THREE.Group|THREE.Object3D} group - Group cần vẽ bounds
   * @param {number} color - Màu của box (hex)
   * @returns {THREE.Box3Helper} Box3Helper object
   */
  createBoundingBoxHelper(group, color = 0x00ff00) {
    const box = new THREE.Box3().setFromObject(group);
    const helper = new THREE.Box3Helper(box, color);
    helper.userData = {
      type: 'boundingBoxHelper',
      moduleUuid: group.uuid,
    };
    return helper;
  }

  /**
   * Xóa tất cả bounding box helpers
   */
  clearBoundingBoxHelpers() {
    const helpersToRemove = Array.from(this.boundingBoxHelpers.values());
    helpersToRemove.forEach(helper => {
      this.extendedSceneManager.scene.remove(helper);
      if (helper.geometry) helper.geometry.dispose();
      if (helper.material) helper.material.dispose();
    });
    this.boundingBoxHelpers.clear();
  }

  /**
   * Vẽ bounding boxes cho selected module và target modules
   */
  drawBoundingBoxes() {
    if (!this.selectedModule) return;

    // Vẽ bound cho selected module (màu vàng)
    const selectedHelper = this.createBoundingBoxHelper(
      this.selectedModule.group,
      0xffff00
    );
    this.boundingBoxHelpers.set('selected', selectedHelper);
    this.extendedSceneManager.scene.add(selectedHelper);

    // Vẽ bound cho các target modules (màu xanh lá)
    const distances = this.getAllDistances(this.selectedModule);
    const targetModules = new Set();

    Object.values(distances).forEach(distanceInfo => {
      if (distanceInfo.targetModule) {
        targetModules.add(distanceInfo.targetModule);
      }
    });

    targetModules.forEach(targetModule => {
      const helper = this.createBoundingBoxHelper(targetModule.group, 0x00ff00);
      this.boundingBoxHelpers.set(targetModule.group.uuid, helper);
      this.extendedSceneManager.scene.add(helper);
    });
  }

  /**
   * Lấy 8 điểm góc (vertices) của bounding box từ một group
   * @param {THREE.Group|THREE.Object3D} group - Group cần lấy bounds
   * @returns {Array<THREE.Vector3>} Mảng 8 điểm góc của bounding box
   */
  getBoundingBoxVertices(group) {
    // Tạo bounding box cho group
    const box = new THREE.Box3().setFromObject(group);

    // Lấy min và max points
    const min = box.min;
    const max = box.max;

    // Tính 8 điểm góc của bounding box
    const vertices = [
      new THREE.Vector3(min.x, min.y, min.z), // 0: bottom-left-back
      new THREE.Vector3(max.x, min.y, min.z), // 1: bottom-right-back
      new THREE.Vector3(max.x, min.y, max.z), // 2: bottom-right-front
      new THREE.Vector3(min.x, min.y, max.z), // 3: bottom-left-front
      new THREE.Vector3(min.x, max.y, min.z), // 4: top-left-back
      new THREE.Vector3(max.x, max.y, min.z), // 5: top-right-back
      new THREE.Vector3(max.x, max.y, max.z), // 6: top-right-front
      new THREE.Vector3(min.x, max.y, max.z), // 7: top-left-front
    ];

    return vertices;
  }

  /**
   * Lấy 6 mặt phẳng (planes) của bounding box từ một group
   * @param {THREE.Group|THREE.Object3D} group - Group cần lấy bounds
   * @returns {Object} Object chứa 6 mặt phẳng với các điểm góc
   */
  getBoundingBoxFaces(group) {
    const vertices = this.getBoundingBoxVertices(group);

    // Định nghĩa 6 mặt, mỗi mặt có 4 điểm góc (theo thứ tự ngược chiều kim đồng hồ)
    return {
      bottom: [vertices[0], vertices[1], vertices[2], vertices[3]], // mặt dưới (y = min)
      top: [vertices[4], vertices[5], vertices[6], vertices[7]], // mặt trên (y = max)
      front: [vertices[3], vertices[2], vertices[6], vertices[7]], // mặt trước (z = max)
      back: [vertices[0], vertices[1], vertices[5], vertices[4]], // mặt sau (z = min)
      left: [vertices[0], vertices[3], vertices[7], vertices[4]], // mặt trái (x = min)
      right: [vertices[1], vertices[2], vertices[6], vertices[5]], // mặt phải (x = max)
    };
  }

  /**
   * Lấy điểm trung tâm từ 4 điểm của một mặt phẳng
   * @param {Array<THREE.Vector3>} facePoints - Mảng 4 điểm góc của mặt phẳng
   * @returns {THREE.Vector3} Điểm trung tâm của mặt phẳng
   */
  getFaceCenter(facePoints) {
    if (!facePoints || facePoints.length !== 4) {
      console.warn('getFaceCenter requires exactly 4 points');
      return new THREE.Vector3();
    }

    const center = new THREE.Vector3();

    // Tính trung bình của 4 điểm
    facePoints.forEach(point => {
      center.add(point);
    });

    center.divideScalar(4);

    return center;
  }

  /**
   * Tìm điểm giao giữa đường thẳng (từ centerModule đến centerFace) và bounding box của module khác
   * Sử dụng bounding box thay vì mesh geometry để tránh vấn đề module thiếu mặt
   * @param {THREE.Vector3} centerModule - Điểm trung tâm của module
   * @param {THREE.Vector3} centerFace - Điểm trung tâm của mặt
   * @param {THREE.Group|THREE.Object3D} targetGroup - Module/group cần kiểm tra giao
   * @returns {THREE.Vector3|null} Điểm giao hoặc null nếu không có giao điểm
   */
  getIntersectionPoint(centerModule, centerFace, targetGroup) {
    // Tạo direction vector từ centerModule đến centerFace
    const direction = new THREE.Vector3()
      .subVectors(centerFace, centerModule)
      .normalize();

    // Tạo Ray từ centerModule theo direction
    const ray = new THREE.Ray(centerModule, direction);

    // Lấy bounding box của target
    const targetBox = new THREE.Box3().setFromObject(targetGroup);

    // Tính intersection với bounding box
    const intersectionPoint = new THREE.Vector3();
    const hasIntersection = ray.intersectBox(targetBox, intersectionPoint);

    if (!hasIntersection) {
      return null;
    }

    // Kiểm tra intersection point có nằm về phía trước centerFace không
    const distanceFromModule = centerModule.distanceTo(intersectionPoint);
    const distanceModuleToFace = centerModule.distanceTo(centerFace);

    // Intersection phải xa hơn centerFace so với centerModule
    if (distanceFromModule > distanceModuleToFace - 0.001) {
      return intersectionPoint;
    }

    return null;
  }

  getAllDistances = module => {
    // Placeholder function - implement actual distance calculation logic here

    const boxModule = new THREE.Box3().setFromObject(module.group);
    const centerModule = boxModule.getCenter(new THREE.Vector3());

    const faces = this.getBoundingBoxFaces(module.group);

    // Lấy tất cả các modules khác
    const allModules = Array.from(this.getModules().values());
    const result = {};

    Object.entries(faces).forEach(([direction, points]) => {
      const centerFace = this.getFaceCenter(points);
      // Sử dụng center để tính khoảng cách đến các objects khác
      let closestIntersection = null;
      let minDistance = Infinity;

      // Tìm module gần nhất trong hướng này
      allModules.forEach(targetModule => {
        if (targetModule.group.uuid === module.group.uuid) {
          return; // Skip chính nó
        }

        const intersectionPoint = this.getIntersectionPoint(
          centerModule,
          centerFace,
          targetModule.group
        );

        if (intersectionPoint) {
          const distance = centerFace.distanceTo(intersectionPoint);

          if (distance < minDistance) {
            minDistance = distance;
            closestIntersection = {
              point: intersectionPoint,
              distance,
              targetModule,
            };
          }
        }
      });
      result[direction] = {
        distance: closestIntersection ? closestIntersection.distance : null,
        targetPoints: closestIntersection
          ? [centerFace, closestIntersection.point]
          : null,
        intersectionPoint: closestIntersection
          ? closestIntersection.point
          : null,
        targetModule: closestIntersection
          ? closestIntersection.targetModule
          : null,
      };
    });

    return result;
  };

  /**
   * Tạo một đường line cho khoảng cách theo hướng cụ thể
   */
  createDistanceLine(direction, distanceInfo) {
    if (!distanceInfo.targetPoints || distanceInfo.targetPoints.length < 2) {
      return null;
    }
    const [start, end] = distanceInfo.targetPoints;
    const line = this.extendedSceneManager.createDoubleArrow(
      start,
      end,
      0x000,
      0.1
    );

    // const geometry = new THREE.BufferGeometry().setFromPoints(distanceInfo.targetPoints);
    // const material = new THREE.LineBasicMaterial({
    //     color: 0x200000,
    //     linewidth: this.CONSTANTS.LINE_WIDTH,
    //     transparent: true,
    //     opacity: 1
    // });

    // const line = new THREE.Line(geometry, material);
    line.userData = {
      direction: direction,
      distance: distanceInfo.distance,
      targetModuleId: distanceInfo.module?.id || null,
      type: 'distanceLine',
    };

    return line;
  }

  /**
   * Tạo CSS2D label hiển thị giá trị khoảng cách
   */
  createDistanceLabel(direction, distanceInfo) {
    if (!distanceInfo.targetPoints || distanceInfo.targetPoints.length < 2) {
      return null;
    }

    const [startPoint, endPoint] = distanceInfo.targetPoints;

    const midPoint = this._tempVector3
      .addVectors(startPoint, endPoint)
      .multiplyScalar(0.5);

    // midPoint.y += this.CONSTANTS.LABEL_OFFSET;

    // Tạo div element cho label
    const labelDiv = document.createElement('div');
    labelDiv.className = 'distance-label new';
    labelDiv.textContent = `${roundTo(distanceInfo.distance * 1000, 0)} mm`;
    labelDiv.title = 'Double click to edit';

    labelDiv.dataset.direction = direction;
    labelDiv.dataset.distance = roundTo(distanceInfo.distance * 1000, 0);
    labelDiv.dataset.moduleId = distanceInfo.module?.id || '';
    labelDiv.dataset.editing = 'false';

    // Single click - highlight
    labelDiv.addEventListener('click', e => {
      e.stopPropagation();
      if (labelDiv.dataset.editing === 'false') {
        this.setLabelActive(direction, !labelDiv.classList.contains('active'));
      }
    });

    // Double click - edit mode
    labelDiv.addEventListener('dblclick', e => {
      e.stopPropagation();
      this.enableEditMode(labelDiv, direction, distanceInfo);
    });

    // Tạo CSS2DObject
    const label = new CSS2DObject(labelDiv);
    label.position.copy(midPoint);
    label.center?.set(0.5, 0.5);

    label.userData = {
      direction: direction,
      distance: distanceInfo.distance,
      targetModuleId: distanceInfo.module?.id || null,
      type: 'distanceLabel',
      targetPoints: distanceInfo.targetPoints,
    };

    setTimeout(() => {
      labelDiv.classList.remove('new');
    }, 300);

    return label;
  }

  /**
   * Enable edit mode cho label
   */
  enableEditMode(labelDiv, direction, distanceInfo) {
    if (labelDiv.dataset.editing === 'true') return;

    labelDiv.dataset.editing = 'true';
    const currentValue = parseFloat(labelDiv.dataset.distance);
    const originalContent = labelDiv.textContent;

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'distance-input';
    input.value = currentValue;
    input.placeholder = 'Nhập';

    labelDiv.classList.add('editing');
    labelDiv.textContent = '';
    labelDiv.appendChild(input);

    input.focus();
    input.select();

    const validateAndApply = async () => {
      let newValue = parseFloat(input.value) / 1000;
      if (
        getInputType(input.value) === 'expression' &&
        isValidExpression(input.value)
      ) {
        newValue = roundTo(Math.abs(evaluateExpression(input.value))) / 1000;
      }
      if (isNaN(newValue) || newValue < 0) {
        input.classList.add('invalid');
        setTimeout(() => input.classList.remove('invalid'), 300);
        return false;
      }

      await this.applyNewDistance(direction, newValue, labelDiv);

      //Cập nhật lại MB, VB
      await this.extendedSceneManager.updateAllCountertopAndBacksplash();
      return true;
    };

    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (validateAndApply()) {
          this.disableEditMode(labelDiv, direction);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.disableEditMode(labelDiv, direction, originalContent);
      }
    });

    input.addEventListener('blur', e => {
      setTimeout(() => {
        if (labelDiv.dataset.editing === 'true') {
          if (!validateAndApply()) {
            this.disableEditMode(labelDiv, direction, originalContent);
          } else {
            this.disableEditMode(labelDiv, direction);
          }
        }
      }, 100);
    });

    input.addEventListener('click', e => e.stopPropagation());
    input.addEventListener('dblclick', e => e.stopPropagation());
  }

  /**
   * Disable edit mode và return về label normal
   */
  disableEditMode(labelDiv, direction, content = null) {
    if (labelDiv.dataset.editing === 'false') return;

    labelDiv.dataset.editing = 'false';
    labelDiv.classList.remove('editing');

    if (content) {
      labelDiv.textContent = content;
    } else {
      const distance = parseFloat(labelDiv.dataset.distance);
      labelDiv.textContent = `${roundTo(Math.abs(distance))}mm`;
    }

    labelDiv.classList.add('new');
    setTimeout(() => labelDiv.classList.remove('new'), 300);
  }

  /**
   * Set label active state
   */
  setLabelActive(direction, active) {
    const label = this.distanceLabels.get(direction);
    if (label && label.element) {
      if (active) {
        label.element.classList.add('active');
      } else {
        label.element.classList.remove('active');
      }
    }
  }

  // /**
  //  * Apply new distance value và update module position
  //  */
  async applyNewDistance(direction, newDistance, labelDiv) {
    if (!this.selectedModule) return;

    const label = this.distanceLabels.get(direction);
    if (!label || !label.userData.targetPoints) return;

    const [startPoint, endPoint] = label.userData.targetPoints;

    // 1. Tính direction vector từ endPoint về startPoint (ngược lại)
    const dir = new THREE.Vector3()
      .subVectors(startPoint, endPoint)
      .normalize();

    // 2. Tính centerFace mới dựa trên newDistance
    const newCenterFace = new THREE.Vector3()
      .copy(endPoint)
      .add(dir.clone().multiplyScalar(newDistance));

    // 3. Lấy bounding box của module để tính offset từ center đến face
    const boxModule = new THREE.Box3().setFromObject(this.selectedModule.group);
    const centerModule = boxModule.getCenter(new THREE.Vector3());

    // 4. Tính offset từ position hiện tại đến center (để giữ nguyên pivot)
    const currentPosition = this.selectedModule.group.position;
    const offsetToCenter = new THREE.Vector3(
      centerModule.x - currentPosition.x,
      centerModule.y - currentPosition.y,
      centerModule.z - currentPosition.z
    );

    // 5. Tính khoảng cách từ centerModule đến startPoint ban đầu
    const distanceCenterToFace = centerModule.distanceTo(startPoint);

    // 6. Tính centerModule mới
    // Vector từ newCenterFace về phía trong module
    const newCenterModule = new THREE.Vector3()
      .copy(newCenterFace)
      .add(dir.clone().multiplyScalar(distanceCenterToFace));

    // 7. Tính position mới = center mới - offset ban đầu (giữ nguyên pivot)
    const newX = newCenterModule.x - offsetToCenter.x;
    const newY = newCenterModule.y - offsetToCenter.y;
    const newZ = newCenterModule.z - offsetToCenter.z;

    // 8. Kiểm tra collision
    const newModuleData = {
      ...this.selectedModule,
      x: newX,
      y: newY,
      z: newZ,
    };

    // 9. Kiểm tra va chạm với tất cả modules khác
    const collision = this.checkCollisionWithAllModules(
      newModuleData,
      this.selectedModule.group,
      this.selectedModule.group.uuid
    );

    if (collision) {
      openMessageError({
        content: `${i18n.t('Collides with')} ${
          collision.collidedModule.type || 'module khác'
        }!`,
      });
      return;
    }

    // 10. Update module position
    this.selectedModule.group.position.set(newX, newY, newZ);
    // this.selectedModule.x = newX;
    // this.selectedModule.y = newY;
    // this.selectedModule.z = newZ;

    // 9. Emit event for external handlers
    if (this.onDistanceChanged) {
      this.onDistanceChanged({
        ...this.selectedModule,
        x: newX,
        y: newY,
        z: newZ,
      });
    }

    // 10. Update all distance lines and labels
    this.updateDistanceLines();

    if (
      this.extendedSceneManager?.dimensionSystem?.cornerPoints.length > 0 &&
      this.extendedSceneManager
    ) {
      this.extendedSceneManager.clearAllCornerPoints();
      this.extendedSceneManager.generateCornerPoints();
    }
    await this.extendedSceneManager.calculateTotalCost({});
  }

  /**
   * Kiểm tra 2 modules có va chạm không
   * So sánh từng mesh của module1 với từng mesh của module2 thay vì
   * dùng bounding box của cả module (tránh báo va chạm sai với tủ góc,
   * tủ có chân, tủ hở đáy... nơi bounds tổng lớn hơn khối thật)
   * @param {Object} module1 - Module thứ nhất
   * @param {Object} module2 - Module thứ hai
   * @returns {Object} Kết quả kiểm tra va chạm
   */
  checkModuleCollision(module1, module2) {
    if (!module1?.group || !module2?.group) {
      return {
        collision: false,
        type: 'no_collision',
        error: 'Invalid modules',
      };
    }

    // Không check collision với chính nó
    if (module1.group.uuid === module2.group.uuid) {
      return { collision: false, type: 'same_module' };
    }

    const bounds1 =
      module1.meshBounds ?? this.getModuleMeshBounds(module1.group);
    const bounds2 =
      module2.meshBounds ?? this.getModuleMeshBounds(module2.group);

    if (!bounds1.meshes.length || !bounds2.meshes.length) {
      return { collision: false, type: 'no_collision', distance: Infinity };
    }

    // Tolerance 1mm
    const tolerance = 0.001;

    let maxPenetration = 0;
    let collidedMeshes = null;
    let touchingDepth = null;
    let minDistance = Infinity;

    for (const item1 of bounds1.meshes) {
      // Early-out cấp mesh: mesh này không chạm box tổng của module kia
      if (!item1.box.intersectsBox(bounds2.box)) {
        minDistance = Math.min(
          minDistance,
          this.getMinDistanceBetweenBounds(item1.box, bounds2.box)
        );
        continue;
      }

      for (const item2 of bounds2.meshes) {
        if (!item1.box.intersectsBox(item2.box)) {
          minDistance = Math.min(
            minDistance,
            this.getMinDistanceBetweenBounds(item1.box, item2.box)
          );
          continue;
        }

        const penetrationDepth = this.calculatePenetrationDepth(
          item1.box,
          item2.box
        );

        if (penetrationDepth <= tolerance) {
          // 2 mesh chỉ chạm mặt nhau - không coi là lỗi
          minDistance = 0;
          touchingDepth = Math.max(touchingDepth ?? 0, penetrationDepth);
          continue;
        }

        // Nếu có mesh lõm thì AABB chồng nhau chưa chắc khối thật chồng nhau
        // (cột nằm gọn trong phần khuyết của xà ngang) - xác minh lại
        if (
          (item1.isConcave || item2.isConcave) &&
          !this.checkConcaveMeshPenetration(item1, item2, tolerance)
        ) {
          minDistance = 0;
          continue;
        }

        if (penetrationDepth > maxPenetration) {
          maxPenetration = penetrationDepth;
          collidedMeshes = { mesh1: item1.mesh, mesh2: item2.mesh };
        }
      }
    }

    // Có ít nhất 1 cặp mesh xuyên vào nhau - coi là lỗi
    if (collidedMeshes) {
      return {
        collision: true,
        type: 'penetrating',
        penetrationDepth: maxPenetration,
        mesh1: collidedMeshes.mesh1,
        mesh2: collidedMeshes.mesh2,
      };
    }

    if (touchingDepth !== null) {
      return {
        collision: false,
        type: 'touching',
        penetrationDepth: touchingDepth,
      };
    }

    return {
      collision: false,
      type: 'no_collision',
      distance: minDistance,
    };
  }

  /**
   * Lấy mesh bounds của module, dùng lại kết quả cũ nếu module không đổi
   * @param {THREE.Group} group - Group của module
   * @returns {{meshes: Array<{mesh: THREE.Mesh, box: THREE.Box3}>, box: THREE.Box3}}
   * @private
   */
  getModuleMeshBounds(group) {
    const stamp = this.getModuleBoundsStamp(group);
    const cached = this._meshBoundsCache.get(group.uuid);

    if (cached && cached.stamp === stamp) {
      return cached.bounds;
    }

    const bounds = this.computeModuleMeshBounds(group);
    this._meshBoundsCache.set(group.uuid, { stamp, bounds });

    return bounds;
  }

  /**
   * Tạo "dấu vân tay" của module để biết bounds cũ còn dùng được không.
   * Rẻ hơn tính lại bounds vì chỉ đọc matrix, không tạo Box3 nào.
   * Bắt được: module di chuyển/xoay, mesh đổi transform, thêm/bớt/ẩn mesh,
   * geometry bị thay hoặc bị sửa tại chỗ
   * @param {THREE.Group} group - Group của module
   * @returns {number} Hash của trạng thái hiện tại
   * @private
   */
  getModuleBoundsStamp(group) {
    group.updateMatrixWorld();

    let hash = 0;
    const mix = value => {
      hash = (hash * 31 + value) | 0;
    };

    group.traverse(child => {
      if (!child.isMesh || !child.geometry) return;

      mix(child.id);
      mix(child.geometry.id);
      mix(child.geometry.attributes?.position?.version ?? 0);
      mix(child.visible ? 1 : 0);

      const elements = child.matrixWorld.elements;
      for (let i = 0; i < 16; i++) {
        // Làm tròn về 0.1mm để tránh sai số float làm hash đổi liên tục
        mix(Math.round(elements[i] * 1e4));
      }
    });

    return hash;
  }

  /**
   * Xoá cache mesh bounds
   * @param {string} [uuid] - UUID của group cần xoá, bỏ trống để xoá tất cả
   */
  invalidateMeshBoundsCache(uuid = null) {
    if (uuid) {
      this._meshBoundsCache.delete(uuid);
    } else {
      this._meshBoundsCache.clear();
    }
  }

  /**
   * Tính bounding box (world space) của từng mesh có khối thật trong module
   * Bỏ qua helper, mesh template và các mesh đang ẩn
   * @param {THREE.Group} group - Group của module
   * @returns {{meshes: Array<{mesh: THREE.Mesh, box: THREE.Box3}>, box: THREE.Box3}}
   * @private
   */
  computeModuleMeshBounds(group) {
    const meshes = [];
    const box = new THREE.Box3().makeEmpty();

    group.updateMatrixWorld();

    group.traverse(child => {
      if (!child.isMesh || !child.geometry) return;

      // Bỏ qua mesh ẩn (kể cả khi cha của nó bị ẩn)
      let current = child;
      while (current && current !== group.parent) {
        if (!current.visible) return;
        current = current.parent;
      }

      // Bỏ qua các mesh không phải khối thật
      if (
        child.userData?.isHelperBox ||
        child.userData?.type === InsideType.MESH_TEMPLATE ||
        child.userData?.type === InsideType.DOOR_TEMPLATE
      ) {
        return;
      }

      // Chỉ lấy box của riêng geometry mesh này, KHÔNG dùng
      // Box3.setFromObject vì nó gộp cả box của các mesh con.
      // Mesh con vẫn được duyệt riêng ở vòng traverse này nên không sót,
      // mà box của mesh cha không bị phình ra trùm lên phần khuyết
      if (!child.geometry.boundingBox) {
        child.geometry.computeBoundingBox();
      }

      const meshBox = child.geometry.boundingBox
        .clone()
        .applyMatrix4(child.matrixWorld);

      if (meshBox.isEmpty()) return;

      // Bỏ qua panel bị scale về 0 (công thức tủ cho sizeLen = 0 khi
      // panel đó không dùng tới) - không phải khối thật
      const size = meshBox.getSize(this._tempVector3);
      if (size.x < 1e-6 || size.y < 1e-6 || size.z < 1e-6) return;

      // Mesh đã qua createPanelColumn (CSG khoét lỗ cột) có hình lõm,
      // AABB của nó lấp đầy phần khuyết nên phải kiểm tra bằng tam giác thật
      const isConcave = !!child.userData?.columnInfo;

      meshes.push({ mesh: child, box: meshBox, isConcave });
      box.union(meshBox);
    });

    return { meshes, box };
  }

  /**
   * Tính độ sâu xuyên vào giữa 2 bounding boxes
   * = quãng đường ngắn nhất cần đẩy box1 để nó rời hẳn khỏi box2
   *
   * Trên mỗi trục có 2 hướng đẩy được: sang trái (box1.max về tới box2.min)
   * hoặc sang phải (box1.min về tới box2.max). Lấy hướng ngắn hơn.
   * KHÔNG dùng overlap = min(max) - max(min) vì khi box1 nằm gọn trong box2
   * thì công thức đó trả về đúng bằng bề dày box1 chứ không phải quãng
   * đường cần đẩy - với panel mỏng sẽ ra số vô nghĩa và có thể bị xem là
   * "chỉ chạm nhau"
   * @private
   */
  calculatePenetrationDepth(box1, box2) {
    const penetrationX = Math.min(
      box1.max.x - box2.min.x,
      box2.max.x - box1.min.x
    );
    const penetrationY = Math.min(
      box1.max.y - box2.min.y,
      box2.max.y - box1.min.y
    );
    const penetrationZ = Math.min(
      box1.max.z - box2.min.z,
      box2.max.z - box1.min.z
    );

    // Đẩy theo trục nào ngắn nhất thì đó là độ sâu xuyên
    return Math.min(penetrationX, penetrationY, penetrationZ);
  }

  /**
   * Xác minh lại va chạm khi có mesh lõm (bị CSG khoét như xà ngang khuyết cột)
   *
   * Với mesh lõm, AABB lấp đầy phần khuyết nên chồng AABB không có nghĩa là
   * chồng khối thật. Duyệt tam giác thật của mesh lõm để biết nó có nằm trong
   * khối kia không. Box đối phương được co lại `tolerance` để 2 mặt áp sát
   * nhau vẫn không bị coi là đâm vào nhau.
   *
   * @param {{mesh: THREE.Mesh, box: THREE.Box3, isConcave: boolean}} item1
   * @param {{mesh: THREE.Mesh, box: THREE.Box3, isConcave: boolean}} item2
   * @param {number} tolerance - Sai số cho phép (m)
   * @returns {boolean} true nếu thực sự đâm vào nhau
   * @private
   */
  checkConcaveMeshPenetration(item1, item2, tolerance) {
    const testAgainst = (item, targetBox) => {
      this._shrunkBox.copy(targetBox).expandByScalar(-tolerance);

      // Box mỏng hơn 2*tolerance sẽ rỗng sau khi co - dùng lại box gốc
      const box = this._shrunkBox.isEmpty() ? targetBox : this._shrunkBox;

      return this.hasTriangleInsideBox(item.mesh, box);
    };

    // Cả 2 cùng lõm: chỉ báo va chạm khi cả 2 chiều đều xác nhận,
    // tránh việc mesh này lọt vào đúng phần khuyết của mesh kia
    if (item1.isConcave && item2.isConcave) {
      return testAgainst(item1, item2.box) && testAgainst(item2, item1.box);
    }

    // Chỉ 1 mesh lõm: box của mesh còn lại là chính xác nên tin được
    return item1.isConcave
      ? testAgainst(item1, item2.box)
      : testAgainst(item2, item1.box);
  }

  /**
   * Kiểm tra mesh có tam giác nào cắt vào box không (world space)
   * @param {THREE.Mesh} mesh - Mesh cần duyệt
   * @param {THREE.Box3} box - Box cần kiểm tra
   * @returns {boolean}
   * @private
   */
  hasTriangleInsideBox(mesh, box) {
    const position = mesh.geometry?.attributes?.position;
    if (!position) return false;

    const index = mesh.geometry.index;
    const count = index ? index.count : position.count;
    const matrixWorld = mesh.matrixWorld;

    for (let i = 0; i < count; i += 3) {
      const i0 = index ? index.getX(i) : i;
      const i1 = index ? index.getX(i + 1) : i + 1;
      const i2 = index ? index.getX(i + 2) : i + 2;

      this._triA.fromBufferAttribute(position, i0).applyMatrix4(matrixWorld);
      this._triB.fromBufferAttribute(position, i1).applyMatrix4(matrixWorld);
      this._triC.fromBufferAttribute(position, i2).applyMatrix4(matrixWorld);

      this._triangle.set(this._triA, this._triB, this._triC);

      if (box.intersectsTriangle(this._triangle)) return true;
    }

    return false;
  }

  /**
   * Tính khoảng cách min giữa 2 bounds
   * @private
   */
  getMinDistanceBetweenBounds(box1, box2) {
    if (box1.intersectsBox(box2)) {
      return 0;
    }

    const dx = Math.max(
      0,
      Math.max(box2.min.x - box1.max.x, box1.min.x - box2.max.x)
    );
    const dy = Math.max(
      0,
      Math.max(box2.min.y - box1.max.y, box1.min.y - box2.max.y)
    );
    const dz = Math.max(
      0,
      Math.max(box2.min.z - box1.max.z, box1.min.z - box2.max.z)
    );

    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /**
   * Kiểm tra va chạm với tất cả modules xung quanh
   * @param {Object} moduleData - Data của module cần kiểm tra (có x, y, z mới)
   * @param {THREE.Group} moduleGroup - Group của module
   * @param {string} excludeUuid - UUID của module cần bỏ qua
   * @returns {Object|null} Thông tin va chạm hoặc null
   */
  checkCollisionWithAllModules(moduleData, moduleGroup, excludeUuid) {
    const allModules = Array.from(this.getModules().values());

    // Dọn cache của các module đã bị xoá khỏi scene
    if (this._meshBoundsCache.size > allModules.length) {
      const liveUuids = new Set(allModules.map(item => item.group.uuid));
      for (const uuid of this._meshBoundsCache.keys()) {
        if (!liveUuids.has(uuid)) {
          this._meshBoundsCache.delete(uuid);
        }
      }
    }

    // Tạo temporary module với vị trí mới
    const tempGroup = moduleGroup.clone();
    tempGroup.position.set(moduleData.x, moduleData.y, moduleData.z);
    tempGroup.updateMatrixWorld(true);

    // Tính bounds 1 lần rồi dùng lại cho mọi target module.
    // Không đưa vào cache vì tempGroup có uuid mới mỗi lần gọi
    const tempModule = {
      group: tempGroup,
      meshBounds: this.computeModuleMeshBounds(tempGroup),
    };

    for (const targetModule of allModules) {
      // Skip chính nó
      if (targetModule.group.uuid === excludeUuid) {
        continue;
      }

      // Kiểm tra collision
      const result = this.checkModuleCollision(tempModule, targetModule);

      // Chỉ báo lỗi nếu penetrating
      if (result.collision && result.type === 'penetrating') {
        return {
          hasCollision: true,
          collidedModule: targetModule,
          type: result.type,
          penetrationDepth: result.penetrationDepth,
        };
      }
    }

    return null;
  }

  /**
   * Cập nhật tất cả distance lines hiện tại
   */
  updateDistanceLines() {
    this.clearDistanceLines();

    const distances = this.getAllDistances(this.selectedModule);

    Object.entries(distances).forEach(([direction, distanceInfo]) => {
      if (distanceInfo.distance !== null && distanceInfo.targetPoints) {
        const line = this.createDistanceLine(direction, distanceInfo);
        if (line) {
          this.distanceLines.set(direction, line);
          this.extendedSceneManager.scene.add(line);
        }

        const label = this.createDistanceLabel(direction, distanceInfo);
        if (label) {
          this.distanceLabels.set(direction, label);
          this.extendedSceneManager.scene.add(label);
        }
      }
    });

    // // Vẽ bounding boxes
    // this.drawBoundingBoxes();
  }

  /**
   * Tạo tất cả distance lines cho selected module
   */
  createDistanceLines() {
    this.clearDistanceLines();
    if (!this.selectedModule) return;

    const distances = this.getAllDistances(this.selectedModule);

    Object.entries(distances).forEach(([direction, distanceInfo]) => {
      if (
        distanceInfo.distance !== null &&
        distanceInfo.targetPoints &&
        !nearlyEqual(roundTo(distanceInfo.distance, 3) * 1000, 0)
      ) {
        const line = this.createDistanceLine(direction, distanceInfo);
        const label = this.createDistanceLabel(direction, distanceInfo);
        if (line) {
          this.distanceLines.set(direction, line);
          this.extendedSceneManager.scene.add(line);
        }
        if (label) {
          this.distanceLabels.set(direction, label);
          this.extendedSceneManager.scene.add(label);
        }
      }
    });

    // // Vẽ bounding boxes
    // this.drawBoundingBoxes();
  }

  rotateSelectedModule180() {
    if (!this.selectedModule) return;

    const group = this.selectedModule.group;

    // Xoay thêm 180 độ quanh trục Y
    group.rotateY(Math.PI);
    group.updateMatrixWorld(true);
  }

  getSelectedModuleDistances() {
    if (!this.selectedModule) return;
    const distances = this.getAllDistances(this.selectedModule);
    const orientation = GeometryUtils.getModuleOrientation(
      this.selectedModule.group
    );

    let mapped;
    switch (orientation) {
      case 'ROTATED_270':
        mapped = {
          back: distances.back,
          right: distances.left,
          front: distances.front,
          left: distances.right,
        };
        break;
      case 'NORMAL':
        mapped = {
          back: distances.left,
          right: distances.front,
          front: distances.right,
          left: distances.back,
        };
        break;
      case 'ROTATED_90':
        mapped = {
          back: distances.front,
          right: distances.right,
          front: distances.back,
          left: distances.left,
        };
        break;
      case 'ROTATED_180':
        mapped = {
          back: distances.right,
          right: distances.back,
          front: distances.left,
          left: distances.front,
        };
        break;
      default:
        mapped = distances;
    }

    this._snapToBackAndNearestSide(mapped);
    return mapped;
  }

  _snapToBackAndNearestSide(mapped) {
    const group = this.selectedModule.group;
    const currentPos = group.position.clone();

    const backDist = mapped.back?.distance ?? Infinity;
    const rightDist = mapped.right?.distance ?? Infinity;
    const leftDist = mapped.left?.distance ?? Infinity;

    const backPoint = mapped.back?.intersectionPoint;
    const rightPoint = mapped.right?.intersectionPoint;
    const leftPoint = mapped.left?.intersectionPoint;

    if (!backPoint) return;

    const useSide = rightDist <= leftDist ? 'right' : 'left';
    const useSideMesh =
      rightDist <= leftDist
        ? mapped.right?.targetModule
        : mapped.left?.targetModule;
    const sideDist = useSide === 'right' ? rightDist : leftDist;
    const sidePoint = useSide === 'right' ? rightPoint : leftPoint;

    if (!sidePoint) return;

    const backDir = new THREE.Vector3()
      .subVectors(backPoint, currentPos)
      .setY(0)
      .normalize();

    const sideDir = new THREE.Vector3()
      .subVectors(sidePoint, currentPos)
      .setY(0)
      .normalize();

    const newPos = currentPos
      .clone()
      .addScaledVector(backDir, backDist)
      .addScaledVector(sideDir, sideDist);

    group.position.copy(newPos);
    group.updateMatrixWorld(true);

    // Lấy lại distances sau khi đã di chuyển
    const newDistances = this.getAllDistances(this.selectedModule);
    const orientation = GeometryUtils.getModuleOrientation(
      this.selectedModule.group
    );

    // Re-map theo orientation để lấy đúng front
    let newMapped;
    switch (orientation) {
      case 'ROTATED_270':
        newMapped = {
          back: newDistances.back,
          right: newDistances.left,
          front: newDistances.front,
          left: newDistances.right,
        };
        break;
      case 'NORMAL':
        newMapped = {
          back: newDistances.left,
          right: newDistances.front,
          front: newDistances.right,
          left: newDistances.back,
        };
        break;
      case 'ROTATED_90':
        newMapped = {
          back: newDistances.front,
          right: newDistances.right,
          front: newDistances.back,
          left: newDistances.left,
        };
        break;
      case 'ROTATED_180':
        newMapped = {
          back: newDistances.right,
          right: newDistances.back,
          front: newDistances.left,
          left: newDistances.front,
        };
        break;
      default:
        newMapped = newDistances;
    }

    const frontDistance = newMapped.front?.distance ?? 0;
    const backDistance = newMapped.back?.distance ?? 0;
    const backMesh = newMapped.back?.targetModule;
    const frontMesh = newMapped.front?.targetModule;

    // So sánh size theo trục phụ thuộc orientation
    const isXAxis =
      orientation === 'ROTATED_270' || orientation === 'ROTATED_90';

    const backSize = backMesh?.size ?? [0, 0, 0];
    const frontSize = frontMesh?.size ?? [0, 0, 0];

    // size = [x, y, z]
    const backSizeVal = isXAxis ? backSize[0] : backSize[2];
    const frontSizeVal = isXAxis ? frontSize[0] : frontSize[2];

    const smallerMesh =
      backSizeVal <= frontSizeVal ? backSizeVal : frontSizeVal;

    const scaleLengthFront = parseFloat(frontDistance.toFixed(3));
    const scaleLengthBack = parseFloat(backDistance.toFixed(3));
    const totalScaleLength = scaleLengthFront + scaleLengthBack;

    const module = this.extendedSceneManager.cabinetManager.getModuleByGltfUuid(
      this.selectedModule.group.uuid
    );
    let totalScaleWidth;
    if (module) {
      const originalWidth =
        module.formularController?.currentDimensions?.width || 1;
      const originalDepth =
        module.formularController?.currentDimensions?.depth || 1;
      module.scaleModuleWithFormular({
        depth: originalDepth + totalScaleLength,
        // width: smallerMesh,
      });
      // module.scaleModuleWithFormular({
      //   depth: originalDepth + totalScaleLength,
      //   width: smallerMesh,
      // });
      totalScaleWidth = Math.abs(smallerMesh - originalWidth);
    }

    switch (orientation) {
      case 'ROTATED_270':
        group.position.z += totalScaleLength / 2;
        // if (useSideMesh.name === 'right') {
        //   group.position.x -= totalScaleWidth / 2;
        // } else {
        //   group.position.x += totalScaleWidth / 2;
        // }
        break;
      case 'NORMAL':
        group.position.x += totalScaleLength / 2;
        // if (useSideMesh.name === 'front') {
        //   group.position.z += totalScaleWidth / 2;
        // } else {
        //   group.position.z -= totalScaleWidth / 2;
        // }
        break;
      case 'ROTATED_90':
        group.position.z -= totalScaleLength / 2;
        // if (useSideMesh.name === 'left') {
        //   group.position.x += totalScaleWidth / 2;
        // } else {
        //   group.position.x -= totalScaleWidth / 2;
        // }
        break;
      case 'ROTATED_180':
        group.position.x -= totalScaleLength / 2;
        // if (useSideMesh.name === 'back') {
        //   group.position.z -= totalScaleWidth / 2;
        // } else {
        //   group.position.z += totalScaleWidth / 2;
        // }
        break;
      default:
        break;
    }

    return {
      frontDistance,
      backSizeVal,
      frontSizeVal,
      smallerMesh,
      orientation,
    };
  }

  // _snapToBackAndNearestSide(mapped) {
  //   const group = this.selectedModule.group;
  //   const currentPos = group.position.clone();

  //   const backDist  = mapped.back?.distance  ?? Infinity;
  //   const rightDist = mapped.right?.distance ?? Infinity;
  //   const leftDist  = mapped.left?.distance  ?? Infinity;

  //   const backPoint  = mapped.back?.intersectionPoint;
  //   const rightPoint = mapped.right?.intersectionPoint;
  //   const leftPoint  = mapped.left?.intersectionPoint;

  //   if (!backPoint) return;

  //   const useSide   = rightDist <= leftDist ? 'right' : 'left';
  //   const sideDist  = useSide === 'right' ? rightDist : leftDist;
  //   const sidePoint = useSide === 'right' ? rightPoint : leftPoint;

  //   if (!sidePoint) return;

  //   // ── 1. Tính toán distances trước khi di chuyển ──────────────────────────
  //   const orientation = GeometryUtils.getModuleOrientation(this.selectedModule.group);
  //   const isXAxis     = orientation === 'ROTATED_270' || orientation === 'ROTATED_90';

  //   const backMesh  = mapped.back?.targetModule;
  //   const frontMesh = mapped.front?.targetModule;

  //   const backSize  = backMesh?.size  ?? [0, 0, 0];
  //   const frontSize = frontMesh?.size ?? [0, 0, 0];

  //   const backSizeVal  = isXAxis ? backSize[0]  : backSize[2];
  //   const frontSizeVal = isXAxis ? frontSize[0] : frontSize[2];

  //   const smallerMesh = backSizeVal <= frontSizeVal ? backSizeVal : frontSizeVal;

  //   const frontDistance = mapped.front?.distance ?? 0;
  //   const backDistance = mapped.back?.distance ?? 0;
  //   const scaleLengthFront   = parseFloat(frontDistance.toFixed(3));
  //   const scaleLengthBack   = parseFloat(backDistance.toFixed(3));

  //   // ── 2. Scale module trước ───────────────────────────────────────────────
  //   const module = this.extendedSceneManager.cabinetManager.getModuleByGltfUuid(this.selectedModule.group.uuid);
  //   if (module) {
  //     const originalDepth = module.formularController?.currentDimensions?.depth || 1;
  //     module.scaleModuleWithFormular({ depth: originalDepth + scaleLengthBack + scaleLengthFront, width: smallerMesh });
  //   }

  //   // ── 3. Dịch chuyển vị trí sau khi scale xong ───────────────────────────
  //   const backDir = new THREE.Vector3()
  //     .subVectors(backPoint, currentPos)
  //     .setY(0)
  //     .normalize();

  //   const sideDir = new THREE.Vector3()
  //     .subVectors(sidePoint, currentPos)
  //     .setY(0)
  //     .normalize();

  //   const newPos = currentPos.clone()
  //     .addScaledVector(backDir, backDist)
  //     .addScaledVector(sideDir, sideDist);

  //   group.position.copy(newPos);
  //   group.updateMatrixWorld(true);

  //   return {
  //     frontDistance,
  //     backSizeVal,
  //     frontSizeVal,
  //     smallerMesh,
  //     orientation,
  //   };
  // }

  addModule(module) {
    // Placeholder function - implement actual logic to add module here
    return module;
  }

  updateModule(moduleId, newProps) {
    // Placeholder function - implement actual logic to update module here
    return { moduleId, ...newProps };
  }
}
